import { lazy, ReactNode } from 'react';
import { RouteObject, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import type { User } from '@/types/auth.types';

const AdminConsoleLayout = lazy(() => import('@/components/admin/AdminConsoleLayout'));
const RegisterTenantView = lazy(() => import('@/views/admin/RegisterTenantView'));
const UsersView = lazy(() => import('@/views/admin/UsersView'));
const RegisterAccountGroupView = lazy(() => import('@/views/admin/RegisterAccountGroupView'));
const AccountGroupsListView = lazy(() => import('@/views/admin/AccountGroupsListView'));
const ScrapeConfigView = lazy(() => import('@/views/admin/ScrapeConfigView'));
const HashtagCampaignsListView = lazy(() => import('@/views/admin/HashtagCampaignsListView'));
const HashtagScrapeConfigView = lazy(() => import('@/views/admin/HashtagScrapeConfigView'));
const RegisterHashtagCampaignView = lazy(() => import('@/views/admin/RegisterHashtagCampaignView'));
const UnauthorizedView = lazy(() => import('@/views/UnauthorizedView'));

const HomeView = lazy(() => import('@/views/HomeView'));
const SocialResonanceView = lazy(() => import('@/views/SocialResonanceView'));
const ListeningView = lazy(() => import('@/views/ListeningView'));
const AdsView = lazy(() => import('@/views/AdsView'));
const CreativeView = lazy(() => import('@/views/CreativeView'));
const AIMeasurementView = lazy(() => import('@/views/AIMeasurementView'));
const TopContentView = lazy(() => import('@/views/TopContentView'));
const HashtagCampaignAnalysisView = lazy(() => import('@/views/HashtagCampaignAnalysisView'));
const CreatorsView = lazy(() => import('@/views/CreatorsView'));
const InfluencerComparisonView = lazy(() => import('@/views/InfluencerComparisonView'));
const InfluencerReportView = lazy(() => import('@/views/InfluencerReportView'));
const LoginView = lazy(() => import('@/views/LoginView'));
const SetPasswordView = lazy(() => import('@/views/auth/SetPasswordView'));
const ForgotPasswordView = lazy(() => import('@/views/auth/ForgotPasswordView'));
const ScheduleView = lazy(() => import('@/views/ScheduleView'));
const ScheduleConnectCallbackView = lazy(() => import('@/views/schedule/ConnectCallbackView'));
const SuperAdminCsvExportView = lazy(() => import('@/views/admin/SuperAdminOverviewView'));
const SuperAdminDashboardView = lazy(() => import('@/views/admin/SuperAdminDashboardView'));
const SuperAdminAlertsView = lazy(() => import('@/views/admin/SuperAdminAlertsView'));
const EmbedSettingsView = lazy(() => import('@/views/admin/EmbedSettingsView'));
const TenantLimitsView = lazy(() => import('@/views/admin/TenantLimitsView'));
const ConversationAlertsView = lazy(() => import('@/views/admin/ConversationAlertsView'));
const AlertRecipientsView = lazy(() => import('@/views/admin/AlertRecipientsView'));
const ClassificationConfigView = lazy(() => import('@/views/admin/ClassificationConfigView'));
const AudienceConfigView = lazy(() => import('@/views/admin/AudienceConfigView'));
const CampaignAnalysisProfileView = lazy(() => import('@/views/admin/CampaignAnalysisProfileView'));
const BrandDocumentView = lazy(() => import('@/views/admin/BrandDocumentView'));
const SovWeightsView = lazy(() => import('@/views/admin/SovWeightsView'));
const VerticalsView = lazy(() => import('@/views/admin/VerticalsView'));
const AccountGroupCountryView = lazy(() => import('@/views/admin/AccountGroupCountryView'));
const PretestLibraryView = lazy(() => import('@/views/pretest/PretestLibraryView'));
const PretestNewView = lazy(() => import('@/views/pretest/PretestNewView'));
const PretestDetailView = lazy(() => import('@/views/pretest/PretestDetailView'));

const PRETEST_ROLES: User['role'][] = ['MEMBER', 'ADMIN', 'SUPER_USER'];

export interface AppRouteMetadata {
  title?: string;
  description?: string;
  requiresAuth?: boolean;
  isPublic?: boolean;
}

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: User['role'][];
}

function shouldRedirectForRoleMismatch(
  allowedRoles: User['role'][] | undefined,
  user: { role: User['role'] } | null | undefined
): boolean {
  return Boolean(allowedRoles && user && !allowedRoles.includes(user.role));
}

const InfluencersRedirect = () => {
  const { search } = useLocation();

  return <Navigate to={{ pathname: '/creators', search }} replace />;
};

export const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (shouldRedirectForRoleMismatch(allowedRoles, user)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export const routes: (RouteObject & AppRouteMetadata)[] = [
  {
    path: '/login',
    element: <LoginView />,
    title: 'Login',
    isPublic: true,
  },
  {
    path: '/accept-invite',
    element: <SetPasswordView mode="invite" />,
    title: 'Accept invite',
    isPublic: true,
  },
  {
    path: '/reset-password',
    element: <SetPasswordView mode="reset" />,
    title: 'Reset password',
    isPublic: true,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordView />,
    title: 'Forgot password',
    isPublic: true,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <HomeView />
      </ProtectedRoute>
    ),
    title: 'Agent',
    description: 'Measure-Ease overview',
  },
  {
    path: 'unauthorized',
    element: (
      <ProtectedRoute>
        <UnauthorizedView />
      </ProtectedRoute>
    ),
    title: 'Unauthorized',
  },
  {
    path: 'social-resonance',
    element: (
      <ProtectedRoute>
        <SocialResonanceView />
      </ProtectedRoute>
    ),
    title: 'Social Resonance',
    description: 'Social media engagement analytics',
  },
  {
    path: 'creative',
    element: (
      <ProtectedRoute>
        <CreativeView />
      </ProtectedRoute>
    ),
    title: 'Creative',
    description: 'Creative performance metrics',
  },
  {
    path: 'media-metrics',
    element: (
      <ProtectedRoute>
        <></>
      </ProtectedRoute>
    ),
    title: 'Media Metrics',
    description: 'Media performance analytics',
  },
  {
    path: 'ai-measurement',
    element: (
      <ProtectedRoute>
        <AIMeasurementView />
      </ProtectedRoute>
    ),
    title: 'AI Measurement',
    description: 'AI-powered measurement tools',
  },
  {
    path: 'top-content',
    element: (
      <ProtectedRoute>
        <TopContentView />
      </ProtectedRoute>
    ),
    title: 'Top 5 content',
    description: 'Ranking of posts by metric',
  },
  {
    path: 'hashtag-campaigns',
    element: (
      <ProtectedRoute>
        <HashtagCampaignAnalysisView />
      </ProtectedRoute>
    ),
    title: 'Hashtag campaigns',
    description: 'Ranking of posts by analysis score',
  },
  {
    path: 'creators',
    element: (
      <ProtectedRoute>
        <CreatorsView />
      </ProtectedRoute>
    ),
    title: 'Creators',
    description: 'Creators dashboard and analytics',
  },
  {
    path: 'influencers',
    element: <InfluencersRedirect />,
    title: 'Influencer Search',
    description: 'Influencer search and analysis',
  },
  {
    path: 'influencer-comparison',
    element: (
      <ProtectedRoute>
        <InfluencerComparisonView />
      </ProtectedRoute>
    ),
    title: 'Influencer comparison',
    description: 'Compare up to 10 influencers and their performance ranking',
  },
  {
    path: 'influencers/informe',
    element: <Navigate to="/influencers/report" replace />,
    title: 'Influencer report',
    description: 'Legacy path, redirects to the influencer report',
  },
  {
    path: 'influencers/report',
    element: (
      <ProtectedRoute>
        <InfluencerReportView />
      </ProtectedRoute>
    ),
    title: 'Influencer report',
    description: 'Printable report for the influencer you looked up',
  },
  {
    path: 'listening',
    element: (
      <ProtectedRoute>
        <ListeningView />
      </ProtectedRoute>
    ),
    title: 'Listening',
    description: "What's being said, across four conversation sources",
  },
  {
    path: 'ads',
    element: (
      <ProtectedRoute>
        <AdsView />
      </ProtectedRoute>
    ),
    title: 'Competitor ads',
    description: 'What competitors are advertising on Meta, and how it changes week to week',
  },
  {
    path: 'pretest',
    element: (
      <ProtectedRoute allowedRoles={PRETEST_ROLES}>
        <PretestLibraryView />
      </ProtectedRoute>
    ),
    title: 'Pre-testing',
    description: 'Score creative before it is published',
  },
  {
    path: 'pretest/new',
    element: (
      <ProtectedRoute allowedRoles={PRETEST_ROLES}>
        <PretestNewView />
      </ProtectedRoute>
    ),
    title: 'Test a post',
  },
  {
    path: 'pretest/:id',
    element: (
      <ProtectedRoute allowedRoles={PRETEST_ROLES}>
        <PretestDetailView />
      </ProtectedRoute>
    ),
    title: 'Pre-test result',
  },
  {
    path: 'schedule',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_USER']}>
        <ScheduleView />
      </ProtectedRoute>
    ),
    title: 'Schedule',
    description: 'Plan and schedule social media posts',
  },
  {
    path: 'schedule/connect/callback',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_USER']}>
        <ScheduleConnectCallbackView />
      </ProtectedRoute>
    ),
    title: 'Connecting account',
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_USER']}>
        <AdminConsoleLayout variant="tenant" />
      </ProtectedRoute>
    ),
    title: 'Admin Panel',
    children: [
      {
        index: true,
        element: <AccountGroupsListView />,
      },
      {
        path: 'users',
        element: <UsersView scope="tenant" />,
      },
      {
        path: 'users/new',
        element: <Navigate to="/admin/users?invite=1" replace />,
      },
      {
        path: 'alert-recipients',
        element: <AlertRecipientsView />,
      },
      {
        path: 'account-groups/new',
        element: <RegisterAccountGroupView />,
      },
      {
        path: 'account-groups/:id/configure',
        element: <ScrapeConfigView />,
      },
      {
        path: 'hashtag-campaigns',
        element: <Navigate to="/admin/hashtag-campaigns/manage" replace />,
      },
      {
        path: 'hashtag-campaigns/manage',
        element: <HashtagCampaignsListView />,
      },
      {
        path: 'hashtag-campaigns/:campaignCode/configure',
        element: <HashtagScrapeConfigView />,
      },
      {
        path: 'hashtag-campaigns/new',
        element: <RegisterHashtagCampaignView />,
      },
    ],
  },
  {
    path: '/super-admin',
    element: (
      <ProtectedRoute allowedRoles={['SUPER_USER']}>
        <AdminConsoleLayout />
      </ProtectedRoute>
    ),
    title: 'Super Admin Panel',
    children: [
      {
        index: true,
        element: <SuperAdminDashboardView />,
      },
      {
        path: 'alerts',
        element: <SuperAdminAlertsView />,
      },
      {
        path: 'csv',
        element: <SuperAdminCsvExportView />,
      },
      {
        path: 'users/new',
        element: <UsersView />,
      },
      {
        path: 'tenants/new',
        element: <RegisterTenantView />,
      },
      {
        path: 'embed-settings',
        element: <EmbedSettingsView />,
      },
      {
        path: 'tenant-limits',
        element: <TenantLimitsView />,
      },
      {
        path: 'conversation-alerts',
        element: <ConversationAlertsView />,
      },
      {
        path: 'classification',
        element: <ClassificationConfigView />,
      },
      {
        path: 'audiences',
        element: <AudienceConfigView />,
      },
      {
        path: 'campaign-analysis',
        element: <CampaignAnalysisProfileView />,
      },
      {
        path: 'brand-documents',
        element: <BrandDocumentView />,
      },
      {
        path: 'account-group-country',
        element: <AccountGroupCountryView />,
      },
      {
        path: 'sov-weights',
        element: <SovWeightsView />,
      },
      {
        path: 'verticals',
        element: <VerticalsView />,
      },
    ],
  },
];

export const ROUTES = {
  LOGIN: '/login',
  ACCEPT_INVITE: '/accept-invite',
  RESET_PASSWORD: '/reset-password',
  FORGOT_PASSWORD: '/forgot-password',
  HOME: '/',
  UNAUTHORIZED: '/unauthorized',
  SOCIAL_RESONANCE: '/social-resonance',
  CREATIVE: '/creative',
  MEDIA_METRICS: '/media-metrics',
  AI_MEASUREMENT: '/ai-measurement',
  TOP_CONTENT: '/top-content',
  HASHTAG_CAMPAIGNS: '/hashtag-campaigns',
  CREATORS: '/creators',
  INFLUENCERS: '/influencers',
  INFLUENCER_COMPARISON: '/influencer-comparison',
  LISTENING: '/listening',
  ADS: '/ads',
  PRETEST: '/pretest',
  PRETEST_NEW: '/pretest/new',
  SCHEDULE: '/schedule',
  SCHEDULE_CONNECT_CALLBACK: '/schedule/connect/callback',
  ADMIN: '/admin',
  SUPER_ADMIN: '/super-admin',
  SUPER_ADMIN_CSV: '/super-admin/csv',
  SUPER_ADMIN_ALERTS: '/super-admin/alerts',
  SUPER_ADMIN_EMBED_SETTINGS: '/super-admin/embed-settings',
  SUPER_ADMIN_TENANT_LIMITS: '/super-admin/tenant-limits',
  SUPER_ADMIN_CONVERSATION_ALERTS: '/super-admin/conversation-alerts',
  SUPER_ADMIN_CLASSIFICATION: '/super-admin/classification',
  SUPER_ADMIN_AUDIENCES: '/super-admin/audiences',
  SUPER_ADMIN_BRAND_DOCUMENTS: '/super-admin/brand-documents',
  SUPER_ADMIN_ACCOUNT_GROUP_COUNTRY: '/super-admin/account-group-country',
  SUPER_ADMIN_SOV_WEIGHTS: '/super-admin/sov-weights',
  SUPER_ADMIN_VERTICALS: '/super-admin/verticals',
} as const;

export const pretestPath = (id: string): string => `${ROUTES.PRETEST}/${encodeURIComponent(id)}`;

export const isPublicPath = (pathname: string): boolean =>
  routes.some(route => route.isPublic && route.path === pathname.replace(/(.)\/+$/, '$1'));
